"use client";

import { useState } from "react";
import { Archivo } from "next/font/google";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";

const archivoSemiBold = Archivo({
  subsets: ["latin"],
  weight: "500",
});

const CARDS_PER_PAGE = 4;

interface StaffMember {
  name: string;
  role: string;
  image: string;
  socials: { platform: string; url: string }[];
}

const staff: StaffMember[] = [
  {
    name: "Emile",
    role: "CEO & Co-fondateur",
    image: "/roster/emile1.png",
    socials: [
      { platform: "instagram", url: "#" },
      { platform: "linkedin", url: "#" },
    ],
  },
  {
    name: "Antoine",
    role: "CEO & Co-fondateur",
    image: "/roster/Rares1.png",
    socials: [
      { platform: "instagram", url: "#" },
      { platform: "linkedin", url: "#" },
    ],
  }
];

const socialIconMap: Record<string, { src: string; alt: string }> = {
  instagram: { src: "/reseaux/insta.png", alt: "Instagram" },
  linkedin: { src: "/reseaux/linkedin.png", alt: "LinkedIn" },
};

export default function StaffSection() {
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(staff.length / CARDS_PER_PAGE));

  const goLeft = () => setPage((prev) => (prev - 1 + totalPages) % totalPages);
  const goRight = () => setPage((prev) => (prev + 1) % totalPages);

  const visible = staff.slice(
    page * CARDS_PER_PAGE,
    page * CARDS_PER_PAGE + CARDS_PER_PAGE
  );

  return (
    <section id="staff" className="pb-24 bg-[#0D0D0D] overflow-hidden">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 h-30 flex items-center justify-center text-center">
        <h2 className={`${archivoSemiBold.className} text-white uppercase tracking-[0.3em] text-2xl`}>
          Staff
        </h2>
      </div>

      <div className="relative max-w-5xl mx-auto px-4 md:px-8">
          {totalPages > 1 && (
            <>
              <button
                onClick={goLeft}
                className="absolute left-0 md:left-2 top-[45%] -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-surface-lighter/80 backdrop-blur hover:bg-accent flex items-center justify-center transition-all duration-300 cursor-pointer"
                aria-label="Précédent"
              >
                <ChevronLeft size={24} className="text-white" />
              </button>

              <button
                onClick={goRight}
                className="absolute right-0 md:right-2 top-[45%] -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-surface-lighter/80 backdrop-blur hover:bg-accent flex items-center justify-center transition-all duration-300 cursor-pointer"
                aria-label="Suivant"
              >
                <ChevronRight size={24} className="text-white" />
              </button>
            </>
          )}

          <div className="grid grid-cols-2 md:grid-cols-2 gap-6 md:gap-12 px-2 md:px-4 justify-items-center">
            {visible.map((member) => (
              <div
                key={member.name}
                className="group text-center w-full max-w-[520px]"
              >
                <div className="relative w-full aspect-[520/754] overflow-hidden bg-surface-light rounded-2xl">
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="absolute inset-0 w-full h-full object-cover grayscale"
                      loading="lazy"
                    />
                  ) : (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-br from-surface-lighter to-surface-light" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-3xl font-bold text-accent-light/30">
                          {member.name.split(" ").pop()}
                        </span>
                      </div>
                    </>
                  )}

                  <div
                    className="absolute bottom-0 left-0 right-0 z-10 px-4 pb-4 pt-16"
                    style={{
                      background: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.85) 100%)",
                    }}
                  >
                    <p className="text-white font-semibold text-base leading-tight">
                      {member.name}
                    </p>
                    <p className="text-white/70 text-sm mt-0.5">
                      {member.role}
                    </p>
                    <div className="flex justify-center gap-2 mt-2">
                      {member.socials.map((social) => {
                        const icon = socialIconMap[social.platform];
                        if (!icon) return null;

                        return (
                          <a
                            key={social.platform}
                            href={social.url}
                            className="group w-7 h-7 rounded-full bg-surface-lighter/80 hover:bg-white backdrop-blur flex items-center justify-center transition-all duration-300 !text-white hover:!text-black"
                            aria-label={social.platform}
                          >
                            <Image
                              src={icon.src}
                              alt={icon.alt}
                              width={20}
                              height={20}
                              className="object-contain"
                            />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {totalPages > 1 && (
          <div className="w-screen relative left-1/2 -translate-x-1/2 mt-8 px-2 md:px-2">
            <div className="flex justify-center gap-2">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 cursor-pointer ${
                  i === page
                    ? "bg-white"
                    : "bg-surface-lighter hover:bg-muted"
                }`}
                aria-label={`Page ${i + 1}`}
              />
            ))}
            </div>
          </div>
        )}
    </section>
  );
}
